import { Scorecard } from '../scorecard/scorecard-next.model';
import {
    createEmpty,
    createEmptyBox,
    createEmptyData,
    datasetKey,
} from '../scorecard/scorecard.model';
import { createReducer } from '../store.model';

import { createHistoryPosition } from './general/create-history-position';
import { getYourTeamEnabled } from './general/get-yourteam-enabled';
import { navigateScorecardHistoryReducer } from './general/navigate-scorecard-history.reducer';
import {
    ActionType,
    ListData,
    ListDataResult,
    NavigateHistory,
    Reload,
    ReloadResult,
    Reset,
    RootClearStateAction,
    SelectPeriodType,
} from './my-scorecard.action';
import { getPeriodTypes, getScoreData } from './my-scorecard.function';
import { defaultState, ScorecardState } from './my-scorecard.state';

const { reducer, builder } = createReducer<ActionType, ScorecardState>(
    defaultState(),
);

builder
    .add<Reload>(ActionType.SCORECARD_CARD__RELOAD, (state, action) => {
        return {
            ...state,
            pending: {
                ...state.pending,
                reload: true,
                hasData: true,
            },
        };
    })
    .add<ReloadResult>(
        ActionType.SCORECARD_CARD__RELOAD_RESULT,
        (state, action) => {
            const boxes =
                action.payload.scorecardBoxes != null
                    ? action.payload.scorecardBoxes
                    : [createEmptyBox()];
            const card = action.payload.scorecardBoxes;

            if (card.length === 0) {
                return {
                    ...state,
                    pending: {
                        ...state.pending,
                        reload: false,
                        hasData: false,
                    },
                };
            }

            const nextState: ScorecardState = {
                ...state,
                pending: {
                    ...state.pending,
                    reload: false,
                    hasData: true,
                },
                user: action.payload.user,
                scorecardHistory: { ...state.scorecardHistory },
                scorecardDataset: { ...state.scorecardDataset },
            };

            // see setTabData()
            // TODO consolidate when main scorecard state no longer uses maps
            boxes.forEach((box) => {
                // this should never fail, but still
                const pt = box.periodTypes.find(
                    (e) => e.periodTypeId === box.initialScorecard.periodType,
                );
                if (pt == null) {
                    return;
                }

                const sh = [...box.scorecardHistory, box.initialScorecard].sort(
                    (a, b) => {
                        const retA = +a.endDate - +b.endDate;
                        if (retA === 0) {
                            return a.scorecardId - b.scorecardId;
                        }
                        return retA;
                    },
                );
                nextState.scorecardHistory[pt.periodTypeId] = sh;

                const key = datasetKey(
                    pt.periodTypeId,
                    box.initialScorecardData.scorecardId,
                );
                nextState.scorecardDataset[key] = box.initialScorecardData;

                /// ///////

                nextState.rootConfig =
                    nextState.rootConfig != null
                        ? nextState.rootConfig
                        : box.rootConfig;
                nextState.periodTypes =
                    nextState.periodTypes != null
                        ? nextState.periodTypes
                        : box.periodTypes;

                nextState.showScorecardDetail =
                    state.showScorecardDetail != null
                        ? state.showScorecardDetail
                        : !(
                              nextState.rootConfig?.scorecardDetail
                                  ?.collapsed ?? false
                          );

                // next period type
                nextState.selectedPeriodType = pt;

                // next history
                nextState.selectedScorecardHistory = sh;

                // next scorecard
                const nextSelSc = {
                    header:
                        box.initialScorecard != null
                            ? box.initialScorecard
                            : createEmpty(),
                    data:
                        box.initialScorecardData != null
                            ? box.initialScorecardData
                            : createEmptyData(),
                };

                nextState.selectedScorecard = nextSelSc;
            });

            nextState.historyPos = createHistoryPosition(
                nextState.selectedScorecardHistory,
                nextState.selectedScorecard,
            );

            nextState.yourTeamEnabled = getYourTeamEnabled(
                nextState.selectedScorecard,
            );
            const scorecardData = getScoreData(
                nextState.selectedScorecard,
                action.payload.user,
            );
            nextState.periodOptions =
                state.periodOptions != null
                    ? state.periodOptions
                    : getPeriodTypes(nextState.periodTypes);
            scorecardData.scorecardSubHeader.isDisabled =
                state.periodOptions?.length === 1;
            nextState.scorecardData = scorecardData;

            return nextState;
        },
    );

builder
    .add<ListData>(ActionType.SCORECARD_CARD__LIST_DATA, (state) => {
        return {
            ...state,
            pending: {
                ...state.pending,
                listData: true,
            },
        };
    })
    .add<ListDataResult>(
        ActionType.SCORECARD_CARD__LIST_DATA_RESULT,
        (state, action) => {
            // TODO consolidate when main scorecard state no longer uses maps
            // next dataset
            const dats = [...Object.values(state.scorecardHistory)].reduce(
                (p, c) => [...p, ...c],
                [],
            );
            const nextScs = { ...state.scorecardDataset };
            action.payload.scorecardData.forEach((e) => {
                const sc = dats.find((ee) => ee.scorecardId === e.scorecardId);
                const key = datasetKey(sc.periodType, e.scorecardId);
                nextScs[key] = e;
            });

            return {
                ...state,
                pending: {
                    ...state.pending,
                    listData: false,
                },
                scorecardDataset: nextScs,
            };
        },
    );

builder.add<SelectPeriodType>(
    ActionType.SCORECARD_CARD__SELECT_PERIOD_TYPE,
    (state, action) => {
        // next period type
        const nextPt = state.periodTypes.find(
            (e) => e.periodTypeId === action.payload.periodType,
        );

        // next scorecard history
        const nextSh = state.scorecardHistory[nextPt.periodTypeId];

        // next scorecard
        const head = nextSh[nextSh.length - 1];
        const key = datasetKey(nextPt.periodTypeId, head.scorecardId);
        const nextSc: Scorecard = {
            header: head != null ? head : createEmpty(),
            data:
                state.scorecardDataset[key] != null
                    ? state.scorecardDataset[key]
                    : createEmptyData(),
        };

        const nextHisPos = createHistoryPosition(nextSh, nextSc);

        const nextYtEnabled = getYourTeamEnabled(nextSc);
        const scorecardData = getScoreData(nextSc, state.user);
        const periodOptions = getPeriodTypes(state.periodTypes);
        scorecardData.scorecardSubHeader.isDisabled =
            periodOptions.length === 1;

        return {
            ...state,
            selectedPeriodType: nextPt,
            selectedScorecardHistory: nextSh,
            selectedScorecard: nextSc,
            historyPos: nextHisPos,
            yourTeamEnabled: nextYtEnabled,
            scorecardData,
            periodOptions,
        };
    },
);

builder.add<NavigateHistory>(
    ActionType.SCORECARD_CARD__NAVIGATE_HISTORY,
    (state, action) => {
        return navigateScorecardHistoryReducer(
            state,
            action.payload.historyPosNav,
            state.user,
        );
    },
);

builder.add<Reset>(ActionType.SCORECARD_CARD__RESET, () => {
    return defaultState();
});

builder.add<RootClearStateAction>(
    ActionType.SCORECARD_CARD_ROOT__CLEAR_STATE,
    () => {
        return defaultState();
    },
);

export const scorecardCardReducer = reducer;
